import type { TiptapCollabProviderConfiguration } from '@tiptap-pro/provider'
import { isAxiosError } from 'axios'
import { richTextDocuments } from '@opallabs/api/v3'
import {
  type CollaborationTokenCarrier,
  type CollaborationToken,
  type DocumentId,
} from '../types'

/**
 * If the editor is loaded in browserless for snapshots, there are a couple of
 * considerations to keep in mind:
 *   - The protocol might be `http:` instead of `https:`
 *   - The port needs to be preserved
 */
const url = new URL(window.location.origin + '/tiptap')
if (url.protocol === 'https:') url.protocol = 'wss:'
if (url.protocol === 'http:') url.protocol = 'ws:'
export const TIPTAP_COLLAB_URL = url.toString()

/**
 * Note: a token is valid for a specific set of document ids, so call this again
 * with an updated list of `documentIds` if the set of document ids changes.
 *
 * @param documentIds - The document IDs to request access for
 * @param permission - The permission level: 'readonly' for read-only access, 'write' for writable access,
 *   or undefined (defaults to writable).
 */
export async function getCollaborationToken(
  documentIds: DocumentId | DocumentId[],
  permission?: 'readonly' | 'write'
): Promise<CollaborationTokenCarrier> {
  const wrappedDocumentIds = Array.isArray(documentIds)
    ? documentIds
    : [documentIds]

  // The provider seems to only need the token to initialize the connection
  // and doesn't hold on to it, so it's not necessary to implement
  // token re-authorization. The token does need to be updated if the set of
  // documents changes, though.
  const { access_token, expires_at } = await richTextDocuments.authorize({
    query: {
      document_id: wrappedDocumentIds,
      ...(permission && { permission }),
    },
  })

  return {
    token: access_token as CollaborationToken,
    expiresAt: new Date(expires_at * 1000),
    receivedAt: new Date(),
  }
}

/**
 * Build a config for a collaborative editor session for the given `documentId`.
 *
 * @note As an optimization you can manually call `getCollaborationToken` for
 *   multiple document ids (including `documentId`) and pass the result as
 *   `token` here. If you don’t pass a `token` `getCollaborationToken` will
 *   automatically be called for `documentId`.
 *
 * @param onUnauthorized - Called when the token request gets a 401. The
 *   provider asks for a token on each reconnect, and without a session each
 *   request gets a 401 again, so stop the provider here.
 */
export const getDefaultProviderConfig = (
  documentId: DocumentId,
  token: CollaborationToken | (() => Promise<CollaborationToken>) | undefined,
  onUnauthorized: () => void
) => {
  const config: TiptapCollabProviderConfiguration = {
    baseUrl: TIPTAP_COLLAB_URL,
    name: documentId,
    token: async () => {
      try {
        if (token != null) {
          return typeof token === 'function' ? await token() : token
        }

        return (await getCollaborationToken(documentId)).token
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 401) {
          onUnauthorized()
        }
        throw error
      }
    },
  }
  return config
}
